'use client'
import {
  FlexibleBlockContent,
  MlGeneralWorkspaceCardProps,
  OrCardsBlock,
  OrCardsBlockProps,
  OrContentBlockA,
  OrContentBlockAProps,
  OrFlexibleBlock,
  OrFlexibleBlockProps,
  OrProductDescriptionBlock,
  OrProductDescriptionBlockProps,
  OrSimpleTextBlock,
  OrSimpleTextBlockProps,
  MlSnackbarProps,
  Currency,
  Holiday,
  Media,
  OrBaseContent,
  OrBaseContentProps,
} from '@studio/atomic-ui'
import { mapImage } from '../../services/sanity/client'
import { SanityAtImage, SanityMinimumBookingDuration } from '../../services/sanity/types'
import { BffProduct, ProductSource, ProductType } from '../../types/product'
import { BookingPanelForm } from './booking-panel-form'
import { useBuildings } from '../../components/buildings-provider'
import {
  trackTabClick,
  trackSpaceCardPropertyResultClick,
  trackWorkspacePropertyResultClick,
} from '../../utils/analytics'

export interface TmPdpProps {
  minimumBookingDuration: SanityMinimumBookingDuration
  inquiryOnly: boolean
  isNonStudioLocation: boolean
  inquireMessageSuccess: Omit<MlSnackbarProps, 'dismiss' | 'visible' | 'className'>
  failureMessage: Omit<MlSnackbarProps, 'dismiss' | 'visible' | 'className'>
  textBeforeInquiryPrice?: string
  textAfterInquiryPrice?: string
  inquiryCurrency?: Currency | null
  inquiryPrice?: number
  inquiryRateType?: 'hour' | 'day' | 'week' | 'month'
  bookableDays: number
  id: number
  spaceType: ProductType
  source: ProductSource
  capacity: number
  hideCapacityOnPdp?: boolean
  prices: BffProduct['price'] | null
  availables: number[]
  building: string
  description: Omit<OrProductDescriptionBlockProps, 'images' | 'floorPlanImage'> & {
    media: Media[] | SanityAtImage[]
    floorPlanImage?: SanityAtImage
  }
  spaceCapabilities?: FlexibleBlockContent & { mainTitle: string; images: SanityAtImage[] }
  additionalServices?: OrSimpleTextBlockProps
  location?: OrFlexibleBlockProps
  similarSpaces?: OrCardsBlockProps & { type: 'space' }
  otherWorkspaces?: MlGeneralWorkspaceCardProps[]
  brand?: OrBaseContentProps
  holidays?: Holiday
  earliestBookableHours?: number
}

export const TmPdp = (props: TmPdpProps) => {
  const { getBuildingByKey } = useBuildings()
  return (
    <div>
      <div className="flex lg:pt-12 pb-12 lg:pb-20 lg:px-[10.4375rem] lg:gap-7.5 lg:max-w-screen-lg lg:mx-auto">
        <OrProductDescriptionBlock
          {...props.description}
          images={props.description?.media as Media[]}
          floorPlanImage={props.description?.floorPlanImage ? mapImage(props.description.floorPlanImage) : undefined}
        />

        <BookingPanelForm {...props} />
      </div>
      {props.spaceCapabilities && (
        <div id="tm-pdp-space-capabilities">
          <OrFlexibleBlock
            tabs={[
              {
                label: '',
                content: {
                  ...props.spaceCapabilities,
                  images: props.spaceCapabilities.images.map((img) => mapImage(img, { width: 538 })),
                } as FlexibleBlockContent,
              },
            ]}
            title={props.spaceCapabilities.mainTitle}
          />
        </div>
      )}
      {props.additionalServices && (
        <div id="tm-pdp-additional-services">
          <OrSimpleTextBlock {...props.additionalServices} />
        </div>
      )}
      {props.location && (
        <div id="tm-pdp-location">
          <OrFlexibleBlock
            title={props.location.title}
            tabs={props.location.tabs.map((tab) => ({
              ...tab,
              content: {
                ...tab.content,
                ...('images' in tab.content
                  ? { images: tab.content.images.map((img) => mapImage(img, { width: 538 })) }
                  : {}),
              },
            }))}
            onChange={(index: number) =>
              trackTabClick({
                titleDescriptor: props.location?.tabs[index].label,
                componentName: 'tm-pdp',
                windowLocationHref: window.location.href,
              })
            }
          />
        </div>
      )}
      {props.similarSpaces && (
        <div id="tm-pdp-similar-spaces">
          <OrCardsBlock
            type={props.similarSpaces.type}
            title={props.similarSpaces.title}
            cards={props.similarSpaces.cards.map((tab) => ({
              ...tab,
              content: tab.content?.map((card) => ({
                ...card,
                onClick: () =>
                  trackSpaceCardPropertyResultClick({
                    spaceType: card.space,
                    building: props.building,
                    title: card.title,
                    businessRegion: getBuildingByKey(props.building)?.city.name ?? '',
                    targetUrl: card.href,
                    windowLocationHref: window.location.href,
                  }),
                images: card.images?.map((image) => mapImage(image, { width: 325 })),
              })),
            }))}
          />
        </div>
      )}
      {props.otherWorkspaces && props.otherWorkspaces.length > 0 && (
        <div id="tm-pdp-other-workspaces">
          <OrCardsBlock
            type="workspace"
            title={`Other workspace solutions at ${getBuildingByKey(props.building)?.displayName ?? ''}`}
            cards={props.otherWorkspaces.map((card) => ({
              ...card,
              onClick: () =>
                trackWorkspacePropertyResultClick({
                  windowLocationHref: window.location.href,
                  targetUrl: card.href,
                  propertyTitle: card.title,
                }),

              images: card.images?.map((image) => mapImage(image, { width: 538 })),
            }))}
          />
        </div>
      )}
      {props.brand && (
        <div id="tm-pdp-brand">
          <OrBaseContent {...props.brand} />
        </div>
      )}
    </div>
  )
}
